import { sum } from 'lodash';

import { ACTIONS_NODE_SIZE } from 'dashboard/apps/InquiryTemplateEditor/ActionsNode/ActionsNode';
import { COMPLETE_STEP_NODE_SIZE } from 'dashboard/apps/InquiryTemplateEditor/CompleteStepNode/CompleteStepNode';
import { CONDITIONAL_NODE_WIDTH } from 'dashboard/apps/InquiryTemplateEditor/ConditionalNode/ConditionalNode';
import { CONDITIONAL_ROOT_SIZE } from 'dashboard/apps/InquiryTemplateEditor/ConditionalRootNode/ConditionalRootNode';
import { CONNECTOR_NODE_SIZE } from 'dashboard/apps/InquiryTemplateEditor/ConnectorNode/ConnectorNode';
import { calculateExpandableScreenFlowWidth } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/shared';
import {
  BRANCH_CONNECTOR_WIDTH,
  CONNECTOR_WIDTH,
} from 'dashboard/apps/InquiryTemplateEditor/ScreenFlow/ScreenFlowElements';
import { InquiryTemplateVersionStepType } from 'dashboard/models/inquiry-template-version';
import { WorkflowStep, WorkflowStepType } from 'dashboard/models/workflow-version';
import { UiStepType } from 'lib/interfaces/StepsConfig';

export const ACTIONS_WIDTH = ACTIONS_NODE_SIZE + CONNECTOR_WIDTH;
export const BRANCHING_WIDTH = CONDITIONAL_NODE_WIDTH + BRANCH_CONNECTOR_WIDTH + CONNECTOR_WIDTH;
export const COMPLETE_WIDTH = COMPLETE_STEP_NODE_SIZE + CONNECTOR_WIDTH;
export const CONDITIONAL_WIDTH =
  CONDITIONAL_ROOT_SIZE + CONDITIONAL_NODE_WIDTH + 2 * CONNECTOR_WIDTH + BRANCH_CONNECTOR_WIDTH;
export const CONNECTOR_NODE_WIDTH = CONNECTOR_NODE_SIZE;

export type ScreenFlowDimens = {
  height: number;
  width: number;
};

export type StepPosition = {
  height: number;
  connectorWidth?: number;
};

export type StepBase = {
  [key: string]: unknown;
  token: string;
  type?: InquiryTemplateVersionStepType | 'workflow';
  workflowStepType?: WorkflowStep['type'];
  branches?: StepBase[][];
  isConnector?: boolean;
};

export interface NodeTypeCounts {
  screenCount: number;
  stackCount: number;
  completeCount: number;
  connectorCount: number;
  actionsCount: number;
  conditionalCount: number;
  branchingCount: number;
}

export const isScreenFlowStack = (step: StepBase): boolean =>
  step.type !== 'workflow' &&
  [
    UiStepType.Document,
    UiStepType.GovernmentId,
    UiStepType.Selfie,
    UiStepType.VerifyWithPersona,
    UiStepType.Loop,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  ].includes(step.type);

/**
 * Helper function for getBranchWidth()
 *
 * Returns counts of 6 possible ScreenFlow node types:
 * 1. Screen: dynamic flow screen (stacks are combined steps, with larger width)
 * 2. Complete: non-UI step which exists Persona flow
 * 3. Connector: reference to an existing screen in the flow
 * 4. Actions: node representing ONLY a linear sequence of workflow actions
 * 5. Conditional: specific workflow branching node representing a single conditional step
 *    and implicit update_next_step actions
 * 6. Branching: any other branching node - currently workflow parallel steps and
 *    screen nodes with multiple buttons or workflow_on_error steps
 *
 * Note: a branch can only have a single branching node, as the "continuation"
 *       of that branch visually is that branching node's 0-index branch.
 *
 * @returns NodeTypeCounts
 */
export const getNodeTypeCounts = (branch: StepBase[]): NodeTypeCounts => {
  // get all non-workflow steps to get separate counts for screen, complete (non-UI), and connector (reference to existing screen)
  const templateSteps = branch.filter((step) => step.type !== 'workflow');
  const stackCount = templateSteps.filter(isScreenFlowStack).length;
  const connectorCount = templateSteps.filter((step) => step.isConnector).length;
  const completeCount = templateSteps.filter(
    (step) => step.type === UiStepType.Complete && !step.isConnector,
  ).length;
  const screenCount = templateSteps.length - completeCount - connectorCount - stackCount;
  const actionsCount = branch.filter(
    (step) => step.type === 'workflow' && step.workflowStepType !== WorkflowStepType.Conditional,
  ).length;
  const branchingNode = branch.find((step) => Array.isArray(step.branches));
  // conditional workflow steps
  const conditionalCount = branchingNode != null && branchingNode.type === 'workflow' ? 1 : 0;
  // screen step with multiple transition components
  const branchingCount = branchingNode != null && branchingNode.type !== 'workflow' ? 1 : 0;
  return {
    screenCount,
    stackCount,
    completeCount,
    connectorCount,
    actionsCount,
    conditionalCount,
    branchingCount,
  };
};

/**
 * Helper function for findBranchOverlap() and buildBranchLengths()
 *
 * Calculates width using counts from getNodeTypeCounts()
 * 1. Screen: stepContentWidth + connector
 * 2. Stack: stack width + connector
 * 3. Complete: complete node size + connector
 * 4. Connector: connector node size (no connector_width because the connector node is a terminal step)
 * 5. Actions: actions node size + connector
 * 6. Conditional: conditional root node width + conditional node width + 2 connectors + 1 branch connector
 * 7. Branching: conditional node width + 1 connector + 1 branch connector
 *
 * @returns width of current branch, or recursively summed width of longest branches
 */
export const getBranchWidth = (
  branch: StepBase[],
  stepContentWidth: number,
  stepTokenToPositionMap: Map<string, StepPosition>,
  recursive = false,
): number => {
  const {
    screenCount,
    stackCount,
    completeCount,
    connectorCount,
    actionsCount,
    conditionalCount,
    branchingCount,
  } = getNodeTypeCounts(branch);
  const branchingNode = branch.find(
    (step) => Array.isArray(step.branches) && step.branches.length > 0,
  );
  const connectorSum = sum(
    branch.map((step) => stepTokenToPositionMap.get(step.token)?.connectorWidth ?? 0),
  );

  const screenWidthSum = screenCount * (stepContentWidth + CONNECTOR_WIDTH);
  const stackWidthSum =
    stackCount * (calculateExpandableScreenFlowWidth(stepContentWidth) + CONNECTOR_WIDTH);
  const completeWidthSum = completeCount * COMPLETE_WIDTH;
  const connectorWidthSum = connectorCount * CONNECTOR_NODE_WIDTH;
  const actionsWidthSum = actionsCount * ACTIONS_WIDTH;
  const conditionalWidthSum = conditionalCount * CONDITIONAL_WIDTH;
  const branchingWidthSum = recursive ? branchingCount * BRANCHING_WIDTH : 0;

  const currentBranchWidth =
    screenWidthSum +
    stackWidthSum +
    completeWidthSum +
    connectorWidthSum +
    actionsWidthSum +
    conditionalWidthSum +
    branchingWidthSum +
    connectorSum;

  return branchingNode && recursive
    ? currentBranchWidth +
        Math.max(
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          ...branchingNode.branches.map((branch) =>
            getBranchWidth(branch, stepContentWidth, stepTokenToPositionMap, true),
          ),
        )
    : currentBranchWidth;
};

/**
 * Helper function for buildBranchLengths()
 * Given the position of the conditional in the top branch, and the bottom branch steps,
 * calculates the extended connector width if the bottom branch width is greater than the top branch subset
 *
 * Mutates stepTokenToPosition map in place to set connectorWidth
 * if extended width needed (defaults to CONNECTOR_WIDTH)
 */
export const findBranchOverlap = (
  branchingIndex: number,
  topBranch: StepBase[],
  bottomBranches: StepBase[][],
  stepContentWidth: number,
  stepTokenToPositionMap: Map<string, StepPosition>,
  token: string,
): void => {
  let connectorWidth = stepTokenToPositionMap.get(token)?.connectorWidth ?? 0;
  // include step if branching from screen, otherwise 1 additional screen width is omitted
  const isConditionalBranch = topBranch[branchingIndex].type === 'workflow';
  const topSliceIndex = isConditionalBranch ? branchingIndex : branchingIndex + 1;

  // add width when branching node is NOT screen
  let topLen = getBranchWidth(
    topBranch.slice(0, topSliceIndex),
    stepContentWidth,
    stepTokenToPositionMap,
  );

  if (isConditionalBranch) {
    // add in the width of the conditional branch node prior to the vertical branch element
    topLen += CONNECTOR_WIDTH + CONDITIONAL_ROOT_SIZE;
  }

  const bottomLen = Math.max(
    ...bottomBranches.map((branch) =>
      getBranchWidth(branch, stepContentWidth, stepTokenToPositionMap, true),
    ),
  );

  if (bottomLen > topLen) {
    /**
     * updated connector width should be the difference + 2x connector width:
     * - the first extends the difference from the 'original' connector width,
     *   as that's taken into account when calculating the difference
     * - the second is to add padding between the bottom branch's last node/connector
     *   and where the vertical branch descends
     */
    connectorWidth = bottomLen - topLen + 2 * CONNECTOR_WIDTH;
  } else if (topLen - bottomLen < CONNECTOR_WIDTH) {
    // extend connector width if top exceeds bottom by less than a single connector width as well
    connectorWidth = topLen - bottomLen + 2 * CONNECTOR_WIDTH;
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  stepTokenToPositionMap.set(token, {
    ...stepTokenToPositionMap.get(token),
    connectorWidth,
  });
};

/**
 * Finds branches in the tree where the top branch has a conditional branch
 * that overlaps with the path of any branches below it.
 *
 * If found, calculates the amount that the connector needs to be extended and
 * recursively updates the branches above it.
 *
 * Ensures that the tree is rendered in a "stacked" manner, preferring horizontal
 * extension when collisions are found to attempt to keep the tree
 * horizontally dense and more scannable.
 *
 * @returns total screen flow width
 */
export const buildBranchLengths = (
  steps: StepBase[],
  stepContentWidth: number,
  stepTokenToPositionMap: Map<string, StepPosition>,
): number => {
  const branchingStep = steps.find((step) => Array.isArray(step.branches));
  if (branchingStep) {
    let branchingIndex = -1;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    branchingStep.branches.forEach((branch) =>
      buildBranchLengths(branch, stepContentWidth, stepTokenToPositionMap),
    );

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    for (let i = branchingStep.branches.length - 1; i > 0; i--) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const topBranch = branchingStep.branches[i - 1];
      branchingIndex = topBranch.findIndex((step) => Array.isArray(step.branches));

      if (branchingIndex !== -1) {
        findBranchOverlap(
          branchingIndex,
          topBranch,
          // pass all branches below current as a lower branch can overlap with a non-neighbor sibling
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          branchingStep.branches.slice(i, branchingStep.branches.length),
          stepContentWidth,
          stepTokenToPositionMap,
          topBranch[branchingIndex].token,
        );
      }
    }
  }

  return getBranchWidth(steps, stepContentWidth, stepTokenToPositionMap, !!branchingStep);
};

/**
 * Sets step heights in step token map.
 * Single step heights are stepContentHeight + 32 padding,
 * Branch step heights are # branches * step height.
 *
 * @returns total screen flow height
 */
export const buildStepHeights = (
  steps: StepBase[],
  stepContentHeight: number,
  stepTokenToPositionMap: Map<string, StepPosition>,
): { height: number; totalHeight: number } => {
  if (steps.length === 0) {
    return { height: stepContentHeight, totalHeight: stepContentHeight };
  }

  const [currentStep, ...restSteps] = steps;

  if (Array.isArray(currentStep?.branches)) {
    const allBranchHeights = currentStep.branches.map((branch, index) => {
      const { height, totalHeight } = buildStepHeights(
        branch,
        stepContentHeight,
        stepTokenToPositionMap,
      );
      if (branch.length) {
        stepTokenToPositionMap.set(branch[0].token, { height });
      }
      return index * stepContentHeight + Math.max(height, totalHeight);
    });

    const height = currentStep.branches.length * stepContentHeight;
    stepTokenToPositionMap.set(currentStep.token, { height });
    return { height, totalHeight: Math.max(...allBranchHeights) };
  } else {
    const { height: stepsHeight, totalHeight } = buildStepHeights(
      restSteps,
      stepContentHeight,
      stepTokenToPositionMap,
    );
    /**
     * use max of prev/current height if step is referenced multiple times in flow
     * and itself has multiple branches, e.g screen step with multiple buttons or a workflow_on_error step
     */
    const height = Math.max(
      stepTokenToPositionMap.get(currentStep.token)?.height ?? 0,
      stepsHeight,
    );
    stepTokenToPositionMap.set(currentStep.token, { height });
    return { height, totalHeight };
  }
};

/**
 *
 * Parent function for building step heights and total screen flow height +
 * branch lengths and calculations for extended connectors
 *
 * @returns ScreenFlow height, width
 */
export const buildScreenFlowDimens = (
  steps: StepBase[],
  stepContentHeight: number,
  stepContentWidth: number,
  stepTokenToPositionMap: Map<string, StepPosition>,
): ScreenFlowDimens => {
  const { totalHeight } = buildStepHeights(steps, stepContentHeight, stepTokenToPositionMap);
  const totalWidth = buildBranchLengths(steps, stepContentWidth, stepTokenToPositionMap);
  return { height: totalHeight, width: totalWidth };
};
